import { useState, useEffect } from "react";
import { useTranslation } from "@/lib/i18n";
import { api } from "@/lib/api";
import {
  Star, Eye, Loader2, AlertTriangle, Zap,
} from "lucide-react";

const MARKET_LABELS = {
  "1": "Vitoria Casa", "X": "Empate", "2": "Vitoria Fora",
  "over25": "Mais 2.5 Golos", "under25": "Menos 2.5 Golos",
  "btts_yes": "Ambas Marcam", "btts_no": "Ambas Nao Marcam",
  "corners_over95": "Cantos > 9.5",
};

export default function TopTips({ bankroll, isAdmin }) {
  const { t, lang } = useTranslation();
  const [tips, setTips] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlockedTips, setUnlockedTips] = useState(new Set());
  const [unlockingTip, setUnlockingTip] = useState(null);

  const isUnlocked = (i) => isAdmin || unlockedTips.has(i);

  const handleWatchAd = (i) => {
    setUnlockingTip(i);
    // Monetag ad trigger
    window.open('https://omg10.com/4/11029694', '_blank');
    setTimeout(() => {
      setUnlockingTip(null);
      setUnlockedTips(prev => new Set([...prev, i]));
    }, 5000);
  };

  useEffect(() => {
    api.getTopTips(bankroll)
      .then(setTips)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [bankroll]);

  

  if (loading) {
    return (
      <div className="bg-surface border border-line rounded-sm p-6 flex items-center justify-center">
        <Loader2 className="w-5 h-5 text-volt animate-spin" />
      </div>
    );
  }

  if (!tips) {
    return null;
  }

  if (tips.tips_count === 0) {
    return (
      <div
        className="bg-surface border border-line rounded-sm p-6 flex flex-col items-center justify-center gap-3 text-center animate-slide-up"
        data-testid="top-tips-empty"
      >
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-volt" />
          <h2 className="font-heading text-lg font-black uppercase tracking-tight">
            {t('topTips')}
          </h2>
        </div>
        <AlertTriangle className="w-6 h-6 text-warning" />
        <p className="text-sm text-txt-secondary max-w-md">
          {t('noTips')}
        </p>
        <p className="text-[11px] font-mono text-txt-secondary">
          Filtros: Prob ≥ {tips.filters?.min_probability || "70%"} | Edge ≥ {tips.filters?.min_edge || "6.5%"} | {tips.total_scanned} {t('loadingGames').replace('...', '')} {tips.leagues_scanned || 12}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-slide-up" data-testid="top-tips-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-volt" />
          <h2 className="font-heading text-lg font-black uppercase tracking-tight">
            {t('topTips')}
          </h2>
        </div>
        <span className="text-[10px] font-mono text-txt-secondary bg-surface px-2 py-1 rounded-sm border border-line">
          {tips.tips_count} tips | {tips.total_scanned} analyzed | {tips.leagues_scanned || 12} leagues
        </span>
      </div>

      {/* Strategy Warning */}
      <div className="flex items-start gap-2 px-3 py-2 bg-warning/5 border border-warning/20 rounded-sm" data-testid="strategy-warning">
        <AlertTriangle className="w-3.5 h-3.5 text-warning shrink-0 mt-0.5" />
        <p className="text-[11px] text-warning leading-relaxed">
  {lang === 'en' ? 'Model optimized for single bets only. Not recommended for accumulators.' :
   lang === 'es' ? 'Modelo optimizado solo para apuestas simples. No recomendado para combinadas.' :
   lang === 'fr' ? 'Modele optimise pour les paris simples uniquement. Non recommande pour les combinés.' :
   lang === 'it' ? 'Modello ottimizzato solo per scommesse singole. Non consigliato per le combo.' :
   tips.strategy_warning}
</p>
      </div>

      {/* Tips Cards - match names + odds always visible, prognóstico blurred */}
      <div className="bg-surface border border-line rounded-sm overflow-hidden">
        <div className="divide-y divide-line/30">
          {tips.tips.map((tip, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid={`top-tip-${i}`}>
              {/* Rank */}
              <div className="w-7 h-7 bg-volt/10 rounded-sm flex items-center justify-center shrink-0">
                <span className="font-mono text-sm font-bold text-volt">#{i + 1}</span>
              </div>

              {/* Match info - ALWAYS visible */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {tip.home_logo && <img src={tip.home_logo} alt="" className="w-4 h-4 object-contain" />}
                  <span className="text-sm font-semibold truncate">{tip.home}</span>
                  <span className="text-txt-secondary text-xs">vs</span>
                  {tip.away_logo && <img src={tip.away_logo} alt="" className="w-4 h-4 object-contain" />}
                  <span className="text-sm truncate">{tip.away}</span>
                </div>

                {/* Prognóstico - blurred if locked */}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-txt-secondary uppercase tracking-widest">{tip.league_code}</span>
                    {tip.time && <span className="text-[10px] font-mono text-txt-secondary">{tip.time}</span>}
                    {tip.day && (
                      <span className={`text-[9px] px-1 py-0.5 rounded-sm font-bold ${
                        tip.day === "HOJE" || tip.day === "TODAY" ? "bg-danger/20 text-danger" : "bg-surface-hover text-txt-secondary"
                      }`}>{tip.day}</span>
                    )}
                  {isUnlocked(i) ? (
                    <>
                      <span className="text-[10px] text-azure font-bold">{MARKET_LABELS[tip.market] || tip.market}</span>
                      <span className="text-[10px] text-txt-secondary">@ {tip.odd}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${
                        tip.probability >= 70 ? "bg-volt/20 text-volt" : "bg-azure/20 text-azure"
                      }`}>
                        {tip.probability}%
                      </span>
                    </>
                  ) : (
                    <span className="text-[10px] text-txt-secondary/40 select-none" style={{ filter: "blur(4px)" }}>
                      Prediction @ 0.00
                    </span>
                  )}
                </div>
              </div>

              {/* Edge + Stake - blurred if locked */}
              <div className="text-right shrink-0">
                {isUnlocked(i) ? (
                  <>
                    <p className="font-mono text-base font-bold text-volt">+{tip.edge}%</p>
                    <p className="text-[10px] text-volt">{tip.stake}EUR</p>
                  </>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    <div style={{ filter: "blur(5px)" }} className="select-none">
                      <p className="font-mono text-base font-bold text-volt">+??%</p>
                    </div>
                    <button
                      onClick={() => handleWatchAd(i)}
                      disabled={unlockingTip === i}
                      className="text-[11px] bg-volt text-obsidian font-bold px-3 py-1.5 rounded-sm hover:bg-volt/80 transition-all flex items-center gap-1"
                    >
                      {unlockingTip === i ? <><Loader2 className="w-3 h-3 animate-spin" /> Loading...</> : <>🔓 FREE</>}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Unlock button inside the card area for non-admin */}
        {!isAdmin && (
          <div className="border-t border-line px-4 py-3 bg-obsidian/50">
            <button
              data-testid="unlock-tips-btn"
              onClick={() => window.open('https://whop.com/betmodel/betmodel-premium/', '_blank')}
              className="w-full bg-volt/10 border border-volt/40 text-volt font-bold uppercase text-xs tracking-wide py-2.5 rounded-sm hover:bg-volt/20 transition-all flex items-center justify-center gap-2"
            >
              <><Zap className="w-3.5 h-3.5" /> Subscribe — $9.99/month</>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
